/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { CameraAltOutlined } from '@mui/icons-material';
import { ClientObject } from '../../params/redux/client';
import CustomInput from '../reusable/input';
import { editDealFirstPhaseSchema } from '../../schema/deal_schema';
import ImageClass from '../../assets';
import LabelWrapper from '../wrapper/label';
import { settingEditClientFlag } from '../../redux/slices/client';
import { useParams } from 'react-router';
import { AppDispatch, RootState } from '../../redux/store';
import { Box, Button, Typography } from '@mui/material';
import { DealEditStepOneParams, DealObject } from '../../params/pipeline';
import { Form, Formik } from 'formik';
import { setEditDeal, setInitialDealStepValues } from '../../redux/slices/deal';
import { useDispatch, useSelector } from 'react-redux';

interface EditStepOneProps {
    setActiveStep: (values: DealEditStepOneParams) => void
}

const getInitialValues = (selectedDeal: DealObject, selectedClient: ClientObject, flags: { isEditClient: boolean }, updateDealValues: DealEditStepOneParams, id: number) => {
    if (flags.isEditClient && location.pathname.includes(`/app/client/${id}/dashboard`)) {
        return {
            deal_id: (selectedClient?.deal?.id || selectedClient?.id) as number,
            deal_name: updateDealValues?.deal_name || selectedClient?.company_name || '',
            deal_email: updateDealValues?.deal_email || selectedClient?.company_email || '',
            deal_phone: updateDealValues?.deal_phone || selectedClient?.contact_number || '',
            deal_address: updateDealValues?.deal_address || selectedClient?.address || '',
            deal_state: updateDealValues?.deal_state || selectedClient?.state || '',
            deal_city: updateDealValues?.deal_city || selectedClient?.city || '',
            deal_zip: updateDealValues?.deal_zip || selectedClient?.zip || '',
            custom_field_1: '',
            custom_field_2: '',
            custom_field_3: '',
            custom_field_4: '',
            custom_field_5: '',
            file: ''
        };
    }
    return {
        deal_id: selectedDeal?.id as number,
        deal_name: updateDealValues?.deal_name || selectedDeal?.title || '',
        deal_email: updateDealValues?.deal_email || selectedDeal?.email || '',
        deal_phone: updateDealValues?.deal_phone || selectedDeal?.phone || '',
        deal_address: updateDealValues?.deal_address || selectedDeal?.address || '',
        deal_state: updateDealValues?.deal_state || selectedDeal?.state || '',
        deal_city: updateDealValues?.deal_city || selectedDeal?.city || '',
        deal_zip: updateDealValues?.deal_zip || selectedDeal?.zip || '',
        custom_field_1: updateDealValues?.custom_field_1 || selectedDeal?.custom_field_1 || '',
        custom_field_2: updateDealValues?.custom_field_2 || selectedDeal?.custom_field_2 || '',
        custom_field_3: updateDealValues?.custom_field_3 || selectedDeal?.custom_field_3 || '',
        custom_field_4: updateDealValues?.custom_field_4 || selectedDeal?.custom_field_4 || '',
        custom_field_5: updateDealValues?.custom_field_5 || selectedDeal?.custom_field_5 || '',
        file: ''
    };
};

const EditStepOne = ({ setActiveStep }: EditStepOneProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams();
    const { selectedDeal, loaders, updateDealValues } = useSelector((state: RootState) => state.deal);
    const { flags, selectedClient } = useSelector((state: RootState) => state.client);
    const initialValues: DealEditStepOneParams = getInitialValues(
        selectedDeal as DealObject,
        selectedClient as ClientObject,
        flags,
        updateDealValues?.firstStep as DealEditStepOneParams,
        id as unknown as number
    );

    const handleSubmit = (values: DealEditStepOneParams) => {
        setActiveStep(values);
    };

    const convertToBase64 = (file: File, setFieldValue: (field: string, value: string | ArrayBuffer | null) => void) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            setFieldValue('file', reader.result);
        };
        reader.onerror = (error) => {
            console.error('Base64 Conversion Error: ', error);
        };
    };

    const handleCancel = () => {
        dispatch(settingEditClientFlag(false));
        dispatch(setEditDeal(false));
        dispatch(setInitialDealStepValues());
    };

    return (<Box>
        <Typography variant="h5" align="center" gutterBottom>
            Update Company Details
        </Typography>
        <Formik initialValues={initialValues} validationSchema={editDealFirstPhaseSchema} onSubmit={handleSubmit}>
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
                <Form>
                    <Box display="flex" flexDirection="column" gap="20px">
                        <Box display="flex" justifyContent="center" width="100%" my={1} position="relative">
                            <label htmlFor="upload-photo">
                                <Box
                                    component="div"
                                    width={100}
                                    height={100}
                                    borderRadius="50%"
                                    overflow="hidden"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    position="relative"
                                    border="2px solid #ddd"
                                    sx={{
                                        cursor: 'pointer'
                                    }}
                                >
                                    <img
                                        src={values.file || ImageClass.userImage}
                                        alt="Company Logo"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            bottom: 0,
                                            right: 0,
                                            backgroundColor: 'rgba(0,0,0,0.6)',
                                            width: '30px',
                                            height: '30px',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <CameraAltOutlined sx={{ color: 'white', fontSize: 18 }} />
                                    </Box>
                                </Box>
                            </label>
                            <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="upload-photo"
                                onChange={(e) => {
                                    if (e.target.files && e.target.files[0]) {
                                        convertToBase64(e.target.files[0], setFieldValue);
                                    }
                                }}
                            />
                        </Box>
                        <CustomInput
                            fullWidth
                            label="Name"
                            placeholder="Type here"
                            name="deal_name"
                            value={values.deal_name}
                            variant="outlined"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.deal_name && Boolean(errors.deal_name)}
                        />
                        <CustomInput
                            fullWidth
                            label="Email"
                            placeholder="Type here"
                            name="deal_email"
                            type="email"
                            value={values.deal_email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            variant="outlined"
                            error={touched.deal_email && Boolean(errors.deal_email)}
                        />
                        <CustomInput
                            type="phone"
                            value={values.deal_phone}
                            onChangeForPhone={handleChange('deal_phone')}
                            onBlur={handleBlur('deal_phone')}
                            error={touched.deal_phone && errors.deal_phone}
                            mandatoryFields={true}
                            fullWidth
                            label="Phone Number"
                            placeholder="201-555-0123"
                            variant="outlined"
                            name="deal_phone"
                        />
                        <Box display="flex" gap="10px">
                            <CustomInput
                                fullWidth
                                label="City"
                                placeholder="Type here"
                                name="deal_city"
                                value={values.deal_city}
                                variant="outlined"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.deal_city && Boolean(errors.deal_city)}
                            />
                            <CustomInput
                                fullWidth
                                label="Address"
                                placeholder="Type here"
                                name="deal_address"
                                value={values.deal_address}
                                variant="outlined"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.deal_address && Boolean(errors.deal_address)}
                            />
                            <CustomInput
                                fullWidth
                                label="State"
                                placeholder="Type here"
                                name="deal_state"
                                value={values.deal_state}
                                variant="outlined"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.deal_state && Boolean(errors.deal_state)}
                            />
                            <CustomInput
                                fullWidth
                                label="Zip Code"
                                placeholder="Type here"
                                name="deal_zip"
                                value={values.deal_zip}
                                variant="outlined"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.deal_zip && Boolean(errors.deal_zip)}
                            /></Box>
                        {!flags.isEditClient && !location.pathname.includes(`/app/client/${id}/dashboard`) && (
                            <Box>
                                <LabelWrapper label="Custom Fields" />
                                <Box display="flex" gap="10px">
                                    <CustomInput
                                        fullWidth
                                        placeholder="Custom Field 1"
                                        name="custom_field_1"
                                        value={values.custom_field_1}
                                        onChange={handleChange}
                                    />
                                    <CustomInput
                                        fullWidth
                                        placeholder="Custom Field 2"
                                        name="custom_field_2"
                                        value={values.custom_field_2}
                                        onChange={handleChange}
                                    />
                                    <CustomInput
                                        fullWidth
                                        placeholder="Custom Field 3"
                                        name="custom_field_3"
                                        value={values.custom_field_3}
                                        onChange={handleChange}
                                    />
                                    <CustomInput
                                        fullWidth
                                        placeholder="Custom Field 4"
                                        name="custom_field_4"
                                        value={values.custom_field_4}
                                        onChange={handleChange}
                                    />
                                    <CustomInput
                                        fullWidth
                                        placeholder="Custom Field 5"
                                        name="custom_field_5"
                                        value={values.custom_field_5}
                                        onChange={handleChange}
                                    />
                                </Box>
                            </Box>
                        )}
                        <Box display="flex" justifyContent="flex-end" gap="10px">
                            <Button variant="outlined" color="secondary" onClick={handleCancel} disabled={loaders.isUpdating}>
                                Cancel
                            </Button>
                            <Button type="submit" variant="contained" color="primary" disabled={loaders.isUpdating}>
                                {loaders.isUpdating ? 'Saving...' : 'Next'}
                            </Button>
                        </Box></Box>
                </Form>)}</Formik>
    </Box>);
};

export default EditStepOne;